import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import ReferralWindow from '@wetransfer/transfer/component/Referral/ReferralWindow';
import { willDrawerBeVisible } from '@wetransfer/transfer/component/TeamsDiscoveryDrawer/TeamsDiscoveryDrawerHelper';
import {
  COOKIE_TRANSFER_TYPE_LINK,
  COOKIE_TRANSFER_TYPE_EMAIL,
} from '@wetransfer/transfer/constant/cookie-transfer-types';
import {
  FF_CROSS_PRODUCT_USAGE_SURVEY_UPSELL,
  FF_REFERRAL_PROGRAM,
} from '@wetransfer/transfer/constant/experiments';
import TransferDetails03AssociateTransfer from '@wetransfer/transfer/constant/experiments/transfer-details-03-associate-transfer';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import Helper from '@wetransfer/transfer/helper';
import generateRandomNumber from '@wetransfer/transfer/helper/generate-pseudo-random-number';
import {
  shouldDisplayUpsellProgressPanel,
  openProgressPanel,
} from '@wetransfer/transfer/helper/progress-panel';
import { createPricingRoute } from '@wetransfer/transfer/helper/routeFactory';
import showPricingPageHelper from '@wetransfer/transfer/helper/showPricingPageToFAExperiment';
import {
  userHasFreeTier,
  userHasPaidTier,
} from '@wetransfer/transfer/helper/user';
import Analytics from '@wetransfer/transfer/modules/analytics';
import { trackAmplitudeEvent } from '@wetransfer/transfer/modules/analytics/vendors/amplitude';
import { amplitudeAction } from '@wetransfer/transfer/modules/analytics/vendors/utils/trackUpload';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import { getVariant } from '@wetransfer/transfer/modules/experiments';
import Router from '@wetransfer/transfer/modules/router';
import { localStorage_key_didSeeSneakPeek } from '@wetransfer/transfer/page/PublishSneakPeek/constants';
import { getPublishSmokeTestFlagVariant } from '@wetransfer/transfer/page/PublishSneakPeek/utils';
import { shouldShowPublishSurvey } from '@wetransfer/transfer/page/PublishSurveyPage/helpers';
import Actions from '@wetransfer/transfer/store/action';
import { TRANSFER_TYPES } from '@wetransfer/transfer/transfer-bundle/constant/transfer-types';

import Email from './email';
import EmailVerificationUpsell from './email-verification-upsell';
import Link from './link';
import Upsell from './upsell';

const getUpsellKeys = () => {
  const upsells = ['upsell_pricing'];

  if (getVariant(FF_CROSS_PRODUCT_USAGE_SURVEY_UPSELL) === 'on') {
    upsells.push('upsell_survey');
  }

  return upsells;
};

export const getRandomUpsellKey = (keys) => {
  // randomly select a key using a RNG (between 0 and keys.length)
  return keys[generateRandomNumber(keys.length)];
};

export class Complete extends React.PureComponent {
  getUpsellKeysPro = () => {
    if (getVariant(FF_CROSS_PRODUCT_USAGE_SURVEY_UPSELL) === 'on') {
      return [
        'pro_pass_upsell_one',
        'pro_pass_upsell_two',
        'pro_pass_upsell_three',
        'upsell_survey',
      ];
    }

    return [
      'pro_pass_upsell_one',
      'pro_pass_upsell_two',
      'pro_pass_upsell_three',
      'pro_pass_upsell_four',
    ];
  };

  state = {
    manualCopy: false,
    copied: false,
    shouldDisplayUpsell: false,
    upsellKey: getRandomUpsellKey(
      userHasPaidTier(this.props.user)
        ? this.getUpsellKeysPro()
        : getUpsellKeys()
    ),
    hasGeneratedQRFromLinkOrEmail: false,
    forcedContentKey: null,
  };

  componentDidMount() {
    const { user, session, route } = this.props;

    this.rememberInCookie();

    if (showPricingPageHelper.isPricingPageExperimentVisible(user)) {
      showPricingPageHelper.setUserUploadCounter(session.domain);
    }

    // Show publish smoke test if applicable
    const publishSmokeTestVariant = getPublishSmokeTestFlagVariant();

    if (
      !Browser.isMobile &&
      publishSmokeTestVariant &&
      !localStorage?.getItem(localStorage_key_didSeeSneakPeek)
    ) {
      Router.navigateTo(publishSmokeTestVariant.route);

      return;
    }

    if (!Browser.isMobile && shouldShowPublishSurvey()) {
      Router.navigateTo('/publish-survey');

      return;
    }

    if (!willDrawerBeVisible() && shouldDisplayUpsellProgressPanel(route)) {
      openProgressPanel();
    }
  }

  handleUpsellClick = () => {
    const { upsellKey } = this.state;

    Analytics.trackSnowplowEvent({
      category: 'upsell',
      action: 'click_upsell_button',
      label: upsellKey,
      property: 'upload_page',
    });
  };

  // Get the current transfer type and create a new Transfer
  onNewTransfer = () => {
    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_continue',
    });

    this.props.createSimilar();
  };

  sendAnotherTransferAfterVerification = () => {
    this.onNewTransfer();

    trackAmplitudeEvent({
      action: amplitudeAction.TRANSFER_SEND,
      additionalProperties: {
        Step: 'Send Transfer Done',
        Action: 'Send Another',
        'Transfer Type': 'Email',
      },
    });
  };

  continueAfterUpsell = () => {
    this.onNewTransfer();

    trackAmplitudeEvent({
      action: amplitudeAction.TRANSFER_SEND,
      additionalProperties: {
        Step: 'Interstitial',
        Action: 'Continue',
      },
    });
  };

  rememberInCookie() {
    const { user, transfer } = this.props;
    // Secure these cookies on non-dev environments, set expiry to 365 days
    const cookieOptions = { secure: !__DEV__, days: 365 };
    // ttype is what current users have, so we can keep using their existing preference
    // when we release the site. ttype = transfer type.
    // For compatibility reasons.. we save it as a number.
    // TODO: REMOVE after rollout, we want sane cookies!
    let transferType = transfer.type;

    if (transferType === 'link') {
      transferType = COOKIE_TRANSFER_TYPE_LINK;
    } else {
      transferType = COOKIE_TRANSFER_TYPE_EMAIL;
    }
    Cookie.set(
      AppConfig.cookies.transferType.name,
      transferType,
      cookieOptions
    );

    // Remember recipients and sender email addresses
    if (transfer.type === 'email') {
      if (!user || !user.loggedIn) {
        let savedAddresses = Cookie.get('wt_to') || '';
        savedAddresses = savedAddresses.split(/\s*,\s*/);

        for (const email of transfer.recipients) {
          if (savedAddresses.indexOf(email.toLowerCase()) !== -1) {
            continue;
          }
          savedAddresses.push(email);
        }
        Cookie.set('wt_to', savedAddresses.join(','), cookieOptions);
      }

      Cookie.set('wt_from', transfer.sender, cookieOptions);
    }
  }

  displayUpsell = () => {
    const { user } = this.props;
    const { upsellKey } = this.state;
    const isFreeUser = userHasFreeTier(user);

    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_send_another',
    });

    const sentCount = Helper.getWTSent();

    const isFirstOrEachForthTransfer = sentCount === 1 || sentCount % 4 === 0;
    const referralProgramFF = getVariant(FF_REFERRAL_PROGRAM) === 'on';

    if (
      userHasPaidTier(user) &&
      isFirstOrEachForthTransfer &&
      referralProgramFF
    ) {
      return this.setState({
        shouldDisplayUpsell: true,
        upsellKey: 'referral',
      });
    }

    if (user.loggedIn && !isFreeUser) {
      this.onNewTransfer();
    } else {
      this.setState({ shouldDisplayUpsell: true });

      Analytics.trackSnowplowEvent({
        category: 'upsell',
        action: 'show_page',
        label: upsellKey,
        property: 'upload_page',
      });
    }
  };

  navigateToFreeAccountSignUp = () => {
    const { transfer } = this.props;

    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_create_free_account',
    });

    trackAmplitudeEvent({
      action: 'Account Creation Funnel',
      additionalProperties: {
        Step: 'Prompt',
        Action: 'Sign Up',
        Context: 'Transfer Window',
      },
    });

    Router.navigateTo('/sign-up', false, {
      search: `trk=WT202005_dont_verify_anonym_to_fa&tier=free&prefill=true&prefill_email=${encodeURIComponent(
        transfer.sender
      )}`,
    });
  };

  render() {
    const { transfer, user } = this.props;

    const { shouldDisplayUpsell, upsellKey } = this.state;

    const content = {
      emailVerification: () => (
        <EmailVerificationUpsell
          onRequestClose={this.sendAnotherTransferAfterVerification}
          onFreeAccountUpsellClick={this.navigateToFreeAccountSignUp}
          transfer={transfer}
        />
      ),
      upsells: () => {
        if (this.state.upsellKey === 'referral') {
          return <ReferralWindow onClose={this.onNewTransfer} />;
        }

        return (
          <Upsell
            href={createPricingRoute('TT_plans_upload')}
            illustrationType="transfer-upsell_two"
            upsellKey={upsellKey}
            onNewTransfer={this.continueAfterUpsell}
            onHandleUpsellClick={this.handleUpsellClick}
          />
        );
      },
      link: () => <Link onRequestClose={this.displayUpsell} />,
      email: () => <Email onRequestClose={this.displayUpsell} />,
    };

    function getContentKey() {
      if (
        !user.loggedIn &&
        transfer.type === TRANSFER_TYPES.EMAIL &&
        !TransferDetails03AssociateTransfer.isOn()
      ) {
        return 'emailVerification';
      }

      if (shouldDisplayUpsell) {
        return 'upsells';
      }

      return transfer.type;
    }

    return (
      <div className="transfer__window uploader uploader--complete">
        {(content[getContentKey()] || content['email'])()}
      </div>
    );
  }
}

Complete.propTypes = {
  createSimilar: PropTypes.func.isRequired,
  session: PropTypes.object,
  transfer: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  route: PropTypes.object.isRequired,
};

const mapStateToProps = ({ session, route }) => ({
  session,
  route,
});

const mapDispatchToProps = {
  createSimilar: Actions.Transfer.createSimilar,
};

export default connect(mapStateToProps, mapDispatchToProps)(Complete);
